import type { ReactionMap, UserState } from '../types/common';

/**
 * Client mirror of server/constants/reactions.ts — static presentation data
 * for the reaction picker/bar. The server remains the validation authority
 * (locked or unknown keys are rejected there with 403/404).
 */
export type ReactionAlignment = 'true' | 'false';

export interface ReactionDefinition {
  key: string;
  emoji: string;
  label: string;
  alignment: ReactionAlignment;
  unlockedByDefault: boolean;
  order: number;
}

export const REACTION_DEFINITIONS: ReactionDefinition[] = [
  // Base set — unlocked for everyone
  { key: 'facts', emoji: '💯', label: 'Facts', alignment: 'true', unlockedByDefault: true, order: 1 },
  { key: 'fire', emoji: '🔥', label: 'Fire', alignment: 'true', unlockedByDefault: true, order: 2 },
  { key: 'big_brain', emoji: '🧠', label: 'Big Brain', alignment: 'true', unlockedByDefault: true, order: 3 },
  { key: 'cap', emoji: '🧢', label: 'Cap', alignment: 'false', unlockedByDefault: true, order: 4 },
  { key: 'clown', emoji: '🤡', label: 'Clown', alignment: 'false', unlockedByDefault: true, order: 5 },
  { key: 'delusional', emoji: '💀', label: 'Delusional', alignment: 'false', unlockedByDefault: true, order: 6 },
  // Locked — unlockable via achievements / herd / point shop (to come)
  { key: 'goat', emoji: '🐐', label: 'GOAT', alignment: 'true', unlockedByDefault: false, order: 7 },
  { key: 'prophetic', emoji: '🔮', label: 'Prophetic', alignment: 'true', unlockedByDefault: false, order: 8 },
  { key: 'trash', emoji: '🗑️', label: 'Trash', alignment: 'false', unlockedByDefault: false, order: 9 },
];

export const getReactionDefinition = (key: string): ReactionDefinition | undefined =>
  REACTION_DEFINITIONS.find((d) => d.key === key);

/**
 * Whether the user can use a reaction. Stored sessions from before this
 * feature have no unlockedReactions field — treat that as "base set only".
 */
export const isReactionUnlocked = (
  def: ReactionDefinition,
  user: Pick<UserState, 'unlockedReactions'> | null | undefined
): boolean =>
  def.unlockedByDefault || ((user?.unlockedReactions as string[] | undefined) ?? []).includes(def.key);

/** Client twin of the server aggregation: every reaction counts once toward its alignment. */
export const computeAlignmentCounts = (
  reactions: ReactionMap
): { trueReactionCount: number; falseReactionCount: number } => {
  let trueReactionCount = 0;
  let falseReactionCount = 0;

  for (const def of REACTION_DEFINITIONS) {
    const count = reactions[def.key]?.length ?? 0;
    if (def.alignment === 'true') {
      trueReactionCount += count;
    } else {
      falseReactionCount += count;
    }
  }

  return { trueReactionCount, falseReactionCount };
};
